import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { cn } from '@/lib/utils';

/** Ajuste textos, tempos e URLs aqui sem mexer na lógica. */
export const FORM_CONTINUATION_DEFAULTS = {
	/** Logo para fundo claro (mesmo asset da landing pública). */
	logoUrlOnLight:
		'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1770986601/Plumaa_H_psjyrs.png',
	pageTitleSuffix: ' | Plumaa',
} as const;

export type ContinuationStepMock = {
	id: string;
	/** Badge acima do título (estilo landing: pill laranja). */
	eyebrow: string;
	title: string;
	subtitle: string;
	helperText: string;
	durationMs: number;
	activeDotIndex: number;
	totalDots: number;
};

type FormContinuationMockProps = {
	steps?: ContinuationStepMock[];
	logoUrl?: string;
	onFlowFinish?: () => void;
};

const DEFAULT_STEP_DURATION_MS = 3000;

export const FORM_CONTINUATION_MOCK_STEPS: ContinuationStepMock[] = [
	{
		id: 'processing-profile',
		eyebrow: 'Processamento',
		title: 'Gerando seu retrato de Bem-Estar personalizado',
		subtitle: 'Mapeando suas dimensões de energia',
		helperText: 'Isso pode levar alguns segundos',
		durationMs: DEFAULT_STEP_DURATION_MS,
		activeDotIndex: 0,
		totalDots: 3,
	},
];

const DOT_COLORS = [
	'bg-brand-orange',
	'bg-brand-pink',
	'bg-brand-purple',
] as const;

const FormContinuationMock = ({
	steps = FORM_CONTINUATION_MOCK_STEPS,
	logoUrl = FORM_CONTINUATION_DEFAULTS.logoUrlOnLight,
	onFlowFinish,
}: FormContinuationMockProps) => {
	const safeSteps = useMemo(
		() => steps.filter((step) => step.durationMs > 0),
		[steps],
	);
	const [currentStepIndex, setCurrentStepIndex] = useState(0);
	const [progressFill, setProgressFill] = useState(0);

	const currentStep = safeSteps[currentStepIndex];

	useEffect(() => {
		if (!currentStep) return;
		setProgressFill(0);
		const target = 100;
		const duration = Math.max(1, currentStep.durationMs);
		let animationFrameId = 0;
		let startTime: number | null = null;

		const animate = (now: number) => {
			if (startTime === null) startTime = now;
			const elapsed = now - startTime;
			const ratio = Math.min(elapsed / duration, 1);
			const nextValue = target * ratio;
			setProgressFill(nextValue);

			if (ratio < 1) {
				animationFrameId = requestAnimationFrame(animate);
			}
		};

		animationFrameId = requestAnimationFrame(animate);

		return () => cancelAnimationFrame(animationFrameId);
	}, [currentStep?.id, currentStep?.durationMs]);

	useEffect(() => {
		if (!currentStep) return;

		const timer = window.setTimeout(() => {
			if (currentStepIndex < safeSteps.length - 1) {
				setCurrentStepIndex((prev) => prev + 1);
				return;
			}
			onFlowFinish?.();
		}, currentStep.durationMs);

		return () => window.clearTimeout(timer);
	}, [currentStep, currentStepIndex, onFlowFinish, safeSteps.length]);

	if (!currentStep) {
		return null;
	}

	const helmetTitle = `Processando${FORM_CONTINUATION_DEFAULTS.pageTitleSuffix}`;
	const gradientScale = progressFill > 0 ? 10000 / progressFill : 10000;

	return (
		<section className="flex min-h-screen w-full flex-col items-center justify-center bg-white px-5 py-10 sm:px-8 md:py-14 lg:px-12 xl:px-16 2xl:px-20">
			<Helmet>
				<title>{helmetTitle}</title>
				<meta
					name="description"
					content="Estamos processando suas respostas para o seu retrato de bem-estar."
				/>
			</Helmet>

			<div className="flex w-full max-w-none flex-col items-center text-center">
				<img
					src={logoUrl}
					alt="Plumaa"
					className="h-10 w-auto md:h-11 lg:h-12"
					loading="lazy"
				/>

				<div className="md:px-4.5 mt-8 inline-flex items-center rounded-full border border-brand-orange/30 bg-brand-orange/10 px-4 py-1.5 md:py-1.5">
					<span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-orange md:text-[11px]">
						{currentStep.eyebrow}
					</span>
				</div>

				<h1 className="mt-5 w-full text-2xl font-bold leading-tight tracking-tight text-zinc-900 md:text-[2.35rem] lg:text-[2.5rem]">
					{currentStep.title}
				</h1>
				<p className="mt-3 w-full text-base font-medium leading-relaxed text-brand-orange md:text-lg">
					{currentStep.subtitle}
				</p>

				<div className="mt-10 flex items-center justify-center gap-2.5 md:mt-10 md:gap-2.5">
					{Array.from({ length: currentStep.totalDots }).map(
						(_, index) => {
							const colorClass =
								DOT_COLORS[index] ?? 'bg-zinc-300';
							const isActive =
								index === currentStep.activeDotIndex;
							return (
								<span
									key={`${currentStep.id}-${index}`}
									className={cn(
										'size-2.5 rounded-full transition-all duration-300 md:size-3',
										colorClass,
										isActive
											? 'scale-110 opacity-100 shadow-sm'
											: 'opacity-45',
									)}
									aria-hidden
								/>
							);
						},
					)}
				</div>

				<div className="mt-8 w-full md:mt-9">
					<div className="mb-2 flex items-center justify-between text-xs font-semibold text-zinc-500 md:text-sm">
						<span>Progresso</span>
						<span className="tabular-nums text-zinc-700">
							{Math.round(progressFill)}%
						</span>
					</div>
					<div className="h-2 w-full overflow-hidden rounded-full bg-zinc-200">
						<div
							className="h-full rounded-full bg-gradient-to-r from-brand-orange via-brand-pink to-brand-purple bg-left bg-no-repeat"
							style={{
								width: `${progressFill}%`,
								backgroundSize: `${gradientScale}% 100%`,
							}}
						/>
					</div>
				</div>

				<p className="mt-4 w-full text-sm text-zinc-500">
					{currentStep.helperText}
				</p>
			</div>
		</section>
	);
};

export { FormContinuationMock };
